import { useEffect, useLayoutEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { NEW_SIDEBAR_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the new desktop sidebar gate (`/api/flags/new-sidebar`, Conductor `symphonyNewSidebarWeb`).
 *
 * Fails CLOSED to today's rail. A dev-tools Feature toggles override wins over the server. Memoized per page
 * load, and the last answer is kept in this browser so a reload paints the rail it painted last time instead
 * of swapping designs under the reader once the read lands. Only a real answer is remembered.
 */
const GATE_TIMEOUT_MS = 2500;
const LAST_ANSWER_KEY = 'symphony-new-sidebar';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

let cached: Promise<boolean> | null = null;
let known: boolean | null = null;

function readLastAnswer(): boolean | null {
  try {
    const v = window.localStorage.getItem(LAST_ANSWER_KEY);
    return v === null ? null : v === 'true';
  } catch {
    return null;
  }
}

function writeLastAnswer(on: boolean) {
  try {
    window.localStorage.setItem(LAST_ANSWER_KEY, String(on));
  } catch {
    // a blocked store only costs the first-paint guess
  }
}

export function newSidebarEnabled(): Promise<boolean> {
  const forced = toggleOverride(NEW_SIDEBAR_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/new-sidebar`, { credentials: 'include', signal: AbortSignal.timeout(GATE_TIMEOUT_MS) });
      if (res.ok) {
        known = ((await res.json()) as boolean) === true;
        writeLastAnswer(known);
        return known;
      }
    } catch {
      // closed for now; asked again by the next reader
    }
    cached = null;
    return false;
  })();
  return cached;
}

export function useNewSidebarEnabled(): boolean {
  // The server render cannot know the answer, so the first render is always control; the browser's own
  // last answer is applied before paint, and the live read confirms it.
  const [enabled, setEnabled] = useState(false);
  useIsoLayoutEffect(() => {
    const forced = toggleOverride(NEW_SIDEBAR_SPEC);
    const guess = forced !== undefined ? forced === 'true' : (known ?? readLastAnswer());
    if (guess) setEnabled(true);
  }, []);
  useEffect(() => {
    let live = true;
    void newSidebarEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
